import { useRef, useState } from "react";
import { Camera, Check, Loader2, RotateCcw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fileToJpegBase64 } from "@/lib/veeder";
import { useLang, useT } from "@/lib/i18n";
import { METRIC } from "@/lib/units";
import { formatToEighths } from "@/lib/tank";

// Photo of the stick -> AI reads the wet fuel line (and water paste); driver confirms before it fills the box.
export function StickPhoto({ onValue, mode, testid, max }) {
  const t = useT();
  const { lang } = useLang();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true); setRes(null); setErr("");
    try { setRes(await api.scanStick(await fileToJpegBase64(file), lang, METRIC, mode)); }
    catch (x) { setErr(apiError(x) || t("Couldn't read that photo. Try again.")); }
    setBusy(false);
  };
  const tooBig = res && max && res.fuel_inches > max;
  return (
    <div className="stick-photo" data-testid={`${testid}-photo`}>
      <input ref={input} type="file" accept="image/*" capture="environment" hidden onChange={pick} data-testid={`${testid}-photo-input`} />
      <button type="button" className="ghost-btn manway-btn" disabled={busy} onClick={() => input.current?.click()} data-testid={`${testid}-photo-btn`}>
        {busy ? <Loader2 size={16} className="spin" /> : <Camera size={16} />}{t(busy ? "Reading the stick…" : "Read the stick from a photo")}
      </button>
      {!res && !err && !busy && <div className="field-hint">{t("Lay the stick flat in good light. Get the numbers and the wet line in the photo.")}</div>}
      {err && <div className="scan-message tone-danger" data-testid={`${testid}-photo-error`}>{err}</div>}
      {res && (
        <div className={`scan-message tone-${tooBig ? "danger" : res.confidence === "high" ? "safe" : "caution"}`} data-testid={`${testid}-photo-result`}>
          <div><b data-testid={`${testid}-photo-fuel`}>{t("Fuel line: {v}", { v: formatToEighths(res.fuel_inches) })}</b> · {t(res.confidence === "high" ? "clear read" : res.confidence === "medium" ? "fairly sure" : "not sure")}</div>
          {res.water_inches > 0
            ? <div data-testid={`${testid}-photo-water`}><b>{t("Water: {v}. Check for water before you drop.", { v: formatToEighths(res.water_inches) })}</b></div>
            : res.paste_seen && <div data-testid={`${testid}-photo-water`}>{t("Water paste shows no water.")}</div>}
          {res.note && <div className="card-sub">{res.note}</div>}
          {tooBig && <div>{t("That's taller than this tank. Check the tank size or retake the photo.")}</div>}
          <div className="stick-photo-actions">
            {!tooBig && <button type="button" className="primary-btn" onClick={() => { onValue(res.fuel_inches); setRes(null); }} data-testid={`${testid}-photo-use`}><Check size={16} />{t("Use this reading")}</button>}
            <button type="button" className="ghost-btn" onClick={() => setRes(null)} data-testid={`${testid}-photo-dismiss`}><RotateCcw size={16} />{t("Don't use")}</button>
          </div>
        </div>
      )}
    </div>
  );
}
